
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Link } from "../router";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

import { HOME_HREF, NAV_GROUPS, isActiveHref, visibleNavItems } from "./nav";
import { NavLink } from "./NavLink";
import { RailTooltip } from "./RailTooltip";
import styles from "./shell.module.css";
import { UserMenu, type ShellUser } from "./UserMenu";

type Props = {
  pathname: string;
  expanded: boolean;
  rail: boolean;
  overlay: boolean;
  closing: boolean;
  toggleLabel: string;
  onToggle: () => void;
  onNavigate: () => void;
  user: ShellUser | null;
  onSignOut: () => void;
  trapRef: RefObject<HTMLDivElement | null>;
};

export function Sidebar({ pathname, expanded, rail, overlay, closing, toggleLabel, onToggle, onNavigate, user, onSignOut, trapRef }: Props) {
  const { t } = useTranslation();
  const items = visibleNavItems(user?.role ?? null);
  const ToggleIcon = expanded ? PanelLeftClose : PanelLeftOpen;
  const toggle = (
    <button
      type="button"
      className={styles.iconButton}
      aria-label={toggleLabel}
      aria-expanded={expanded}
      aria-controls="admin-sidebar"
      onClick={onToggle}
    >
      <ToggleIcon className={styles.navIcon} strokeWidth={1.75} aria-hidden="true" />
    </button>
  );

  return (
    <aside
      id="admin-sidebar"
      className={styles.sidebar}
      aria-label={t("nav.shell.mainNavigation")}
      aria-modal={overlay || undefined}
      role={overlay ? "dialog" : undefined}
    >
      <div ref={trapRef} className={styles.sidebarInner}>
        <div className={styles.sidebarHeader}>
          <Link href={HOME_HREF} className={styles.brand} aria-label={t("nav.shell.home")} onClick={onNavigate} inert={closing || undefined}>
            <img src="/mark.svg" alt="" className={styles.brandMark} />
            <span className={styles.brandName} aria-hidden="true">{t("common.brandName")}</span>
          </Link>
          <RailTooltip enabled={rail} content={toggleLabel} describes={false}>
            {toggle}
          </RailTooltip>
        </div>
        <nav className={styles.navScroll} aria-label={t("common.adminSections")} inert={closing || undefined}>
          {NAV_GROUPS.filter((group) => group !== "team").map((group) => (
            <div key={group} role="group" aria-labelledby={`nav-group-${group}`} className={styles.navGroup}>
              <span className={styles.groupSeparator} aria-hidden="true" />
              <p id={`nav-group-${group}`} className={styles.groupLabel}>{t(`nav.group.${group}`)}</p>
              <ul className={styles.navGroup}>
                {items.filter((item) => item.group === group).map((item) => (
                  <li key={item.id}>
                    <NavLink item={item} active={isActiveHref(pathname, item.href)} withTooltip={rail} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className={styles.sidebarFooter} inert={closing || undefined}>
          <ul className={styles.navGroup} aria-label={t("nav.group.team")}>
            {items.filter((item) => item.group === "team").map((item) => (
              <li key={item.id}>
                <NavLink item={item} active={isActiveHref(pathname, item.href)} withTooltip={rail} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
          <UserMenu user={user} onSignOut={onSignOut} side={rail ? "right" : "top"} />
        </div>
      </div>
    </aside>
  );
}
